/*
 * WHICH DOOR A MODEL GOES THROUGH — one rule, both sides of the wire.
 *
 * The edge exposes a key-inject proxy per provider (`/api/llm/<provider>/…`), and something has to
 * decide which one a given model id belongs to. That decision was made TWICE: the worker derived it
 * from the id, and the browser hardcoded `'openrouter'` for every chat turn.
 *
 * The cost of the second spelling was not subtle. Picking Claude sent an Anthropic model id to
 * OpenRouter, which does not recognise a non-namespaced id and answered 400 — so choosing any
 * natively-served vendor failed outright, and the native provider keys the deployment holds were
 * never reached by the chat path at all. The models that DID work were the ones whose ids happen to
 * be OpenRouter-shaped.
 *
 * So it lives here, in the package both the worker and every UI already import, and neither is
 * allowed its own answer.
 */

/**
 * The provider path for a model id.
 *
 * THE SLASH CHECK RUNS FIRST and that ordering is the contract: OpenRouter ids are vendor-namespaced
 * (`vendor/model[:free]`), so `google/gemini-2.0-flash` must ROUTE to OpenRouter — the transport
 * holding the fleet key — even though its BRAND is Google. Confusing the two sends a namespaced id
 * to a vendor that has never heard of it.
 *
 * A bare id belongs to its vendor's own endpoint. Anything unrecognised returns '' — the caller then
 * falls back to the aggregator, which is the honest default for a model this rule has not been
 * taught: OpenRouter fronts the long tail, and guessing a native endpoint for an unknown id would
 * fail where falling back succeeds.
 */
export function providerForModel(model: string): string {
  const m = model.toLowerCase();
  if (m.includes('/')) return 'openrouter';
  if (m.startsWith('gemini')) return 'gemini';
  if (m.startsWith('gpt') || /^o[1-9]/.test(m)) return 'openai';
  if (m.startsWith('claude')) return 'anthropic';
  if (m.startsWith('grok')) return 'xai';
  if (m.startsWith('mistral') || m.startsWith('magistral')) return 'mistral';
  return '';
}

/** The provider path to actually call, with the aggregator as the fallback for anything the rule
 *  does not recognise. What a caller wants when it needs a route rather than a classification. */
export const routeFor = (model: string): string => providerForModel(model) || 'openrouter';

/**
 * The `:variant` an OpenRouter id carries (`vendor/model:variant` → `variant`), or '' for none.
 *
 * NAMESPACED IDS ONLY. A colon in a bare id is somebody else's convention and means something else:
 * an Ollama tag (`qwen2.5:7b`), a Bedrock revision (`…-v1:0`). Reading those as OpenRouter variants
 * would refuse models a BYO endpoint serves perfectly well.
 */
export function variantOf(model: string): string {
  return /^[^/:\s]+\/[^:\s]+:([\w.-]+)$/.exec(model)?.[1].toLowerCase() ?? '';
}

/*
 * LISTINGS THE CHAT ENDPOINT REFUSES OUTRIGHT.
 *
 * OpenRouter publishes `:batch` twins of most frontier models: the same model, served ONLY through
 * its batch API. They sit in `/models` beside the chat listings, declare tool support like them, and
 * differ in nothing but the suffix — and `/chat/completions` turns every one of them away:
 *
 *     google/gemini-3.8-flash:batch cannot be used with the chat/completions endpoint
 *     (adapter VertexGeminiBatchAdapter)
 *
 * Narrower than "has a variant" on purpose. `:free`, `:nitro`, `:online` all answer a chat; whether
 * to OFFER them is the catalog's decision (it offers none), and refusing them here would break a call
 * that works.
 */
const CHAT_REFUSED_VARIANTS = new Set(['batch']);

/** False only for a listing `/chat/completions` will refuse whatever the request says. */
export const chatServable = (model: string): boolean => !CHAT_REFUSED_VARIANTS.has(variantOf(model));

/** The listing of the same model that DOES answer a chat: `vendor/model:batch` → `vendor/model`.
 *  Every `:batch` id on the roster has that twin (all 73 on 2026-10-01). Anything already servable
 *  comes back unchanged. */
export const chatTwin = (model: string): string => (chatServable(model) ? model : model.slice(0, model.lastIndexOf(':')));
